// src/configs/apps/appfunnel/appfunnel.js
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import {
  AppstoreOutlined,
  DashboardOutlined,
  FormOutlined,
  FunnelPlotOutlined,
  GlobalOutlined,
  HomeOutlined,
  MessageOutlined,
  RocketOutlined,
  SearchOutlined,
  SettingOutlined,
  TagsOutlined,
  ToolOutlined,
} from '@ant-design/icons';
import { BsBoxSeam, BsBuilding } from 'react-icons/bs';
import { GrGroup } from 'react-icons/gr';
import { PiToolbox } from 'react-icons/pi';
import { RiShieldUserLine } from 'react-icons/ri';
import { LuDraftingCompass } from 'react-icons/lu';
import { PiUserFocus } from 'react-icons/pi';

// *******************************************************************************
// APP GLOBAL CONFIGURATION
// *******************************************************************************
const APP_FUNNEL = {
  ID: 'appfunnel',
  NAME: 'Itsa.funnel',
  THEME_COLOR: 'CUSTOM_FUNNEL_APP',
  THEME: 'light',
  FINTECH_SERVICES: false,
  DOMAINS: [
    'app.itsafunnel.com',
    'homologation.itsafunnel.com',
    'sistema.itsafunnel.com',
    'homologacao.itsafunnel.com',

    'app.itsafunnel.com.br',
    'homologation.itsafunnel.com.br',
    'sistema.itsafunnel.com.br',
    'homologacao.itsafunnel.com.br',

    'appfunnel-v2.web.app',
    'appfunnel-v2-homologation.web.app',
  ],
  PRESET: {
    MULTI_SUBSCRIPTIONS: true,
    PROFILE: null,
    NATIVE_RESOURCES: ['moduleresources_omnichannel'],
    SUGGESTED_RESOURCES: [],
    CAN_CHANGE_RESOURCES: true,
    SHOW_RESOURCES_FIRST_CONFIGURATION: true,
    SHOW_RESOURCES_SUBSCRIPTION: true,
    CONTRACT: null,
  },
  COPY_RIGHT: 'Itsa.funnel',
  API_PORT_HOMOLOGATION: 8132,
  API_PORT_PRODUCTION: 8032,
  API_HOST_LOCALHOST: 'localhost',
  API_HOST_PRODUCTION: 'vps.jarvisly.com',
  API_INTEGRATED: false,
  TOKEN: import.meta.env.VITE_APPFUNNEL_TOKEN,
  TOKEN_EXP_MINUTES: 60,

  LOCALE: 'pt-BR',
  LANGUAGE: 'pt-BR',
  LANGUAGE_OPTIONS: ['pt-BR', 'pt-PT', 'en-US', 'es-ES'],

  LOGO_APP: '/apps/appfunnel/logo-app.png',
  LOGO_APP_WHITE: '/apps/appfunnel/logo-app-white.png',

  LOGO_APP_ICON: '/apps/appfunnel/logo-app-icon.png',
  LOGO_APP_ICON_WHITE: '/apps/appfunnel/logo-app-icon-white.png',

  LOGO: '/apps/appfunnel/logo.png',
  LOGO_WHITE: '/apps/appfunnel/logo-white.png',
  LOGO_LOGIN_SIDE: '/apps/appfunnel/logo-app-side.png',

  PWA_NAME: { production: 'Itsa.funnel', homologation: 'Itsa.funnel Test' },
  PWA_SHORT_NAME: { production: 'Funnel', homologation: 'Funnel Test' },
  PWA_DESCRIPTION: 'Gestao comercial e CRM para seu negocio',
  TITLE: 'Itsa.funnel',
  SLOGAN: '',
  SLOGAN2: '',
  SLOGAN_REGISTRATION: 'registration_slogan',
  SLOGAN_REGISTRATION2: '',
  APPLICATION_LAYER: 'adm',
  LOGIN_IDENTIFICATION: 'email',

  SELF_REGISTRATION: {
    homologation: false,
    production: false,
  },
  SELF_REGISTRATION_MOBILE_VALIDATION: ['pt-BR'],
  USER_INVITATION_MOBILE_VALIDATION: ['pt-BR'],

  GOOGLE_LOGIN: false,
  FACEBOOK_LOGIN: false,
  RESET_PASSWORD: true,
  NAVIGATION_TYPE: 'side',
  MODULES: ['customers', 'companies', 'dataSearch', 'crm', 'landingPages', 'offers', 'leads'],
  SHOW_ALL_MENUS: false,
  BREADCRUMB_COLOR: '#FAFAFBFF',

  WELCOME_NEXT_STEPS: [
    'explore_the_system',
    'complete_your_profile',
    'create_your_first_subscription',
  ],

  MENU_APPLICATION: [
    {
      path: '/app/account',
      title: 'account_settings',
      icon: SettingOutlined,
      moduleId: 'account',
    },
  ],

  MENU_NAVIGATION: [
    // ******************
    // DASHBOARD
    // ******************
    {
      path: '/app/dashboard/overview',
      title: 'home',
      icon: HomeOutlined,
      moduleId: 'welcome',
      isGroupTitle: true,
    },

    // ******************
    // QUERIES (CONSULTAS)
    // ******************
    {
      path: '/app/queries',
      title: 'queries',
      icon: SearchOutlined,
      isGroupTitle: true,
      // requiredResources: ['moduleresources_lookup'],
      submenu: [
        {
          title: 'people_and_companies',
          icon: SearchOutlined,
          moduleId: 'dataSearch',
          requiredResources: ['moduleresources_lookup'],
        },
      ],
    },

    // ******************
    // REGISTRATIONS (CADASTROS)
    // ******************
    {
      path: '/app/registrations',
      title: 'records',
      icon: FormOutlined,
      isGroupTitle: true,
      submenu: [
        {
          title: 'products',
          icon: BsBoxSeam,
          moduleId: 'products',
          requiredResources: ['moduleresources_products'],
        },
        {
          title: 'services',
          icon: ToolOutlined,
          moduleId: 'services',
          requiredResources: ['moduleresources_services', 'moduleresources_service_orders'],
        },
        // {
        //   title: 'offers',
        //   icon: TagsOutlined,
        //   moduleId: 'offers',
        //   requiredResources: ['moduleresources_crm'],
        // },
        {
          type: 'divider',
        },
        {
          title: 'companies',
          icon: BsBuilding,
          moduleId: 'companies',
          requiredResources: ['moduleresources_multicompany'],
        },
        {
          title: 'customers',
          icon: GrGroup,
          moduleId: 'customers',
          requiredResources: ['moduleresources_crm'],
        },
        {
          title: 'users',
          icon: RiShieldUserLine,
          moduleId: 'users',
          requiredResources: ['moduleresources_multiuser'],
        },
      ],
    },

    // ******************
    // CRM
    // ******************
    {
      path: '/app/commercial',
      title: 'commercial',
      icon: RocketOutlined,
      isGroupTitle: true,
      // requiredResources: ['moduleresources_crm', 'moduleresources_omnichannel'],
      submenu: [
        {
          title: 'omnichannel',
          icon: MessageOutlined,
          moduleId: 'omnichannel',
          requiredResources: ['moduleresources_omnichannel'],
        },

        {
          title: 'landing_pages',
          icon: LuDraftingCompass,
          moduleId: 'landingPages',
          requiredResources: ['moduleresources_landingpages'],
        },

        {
          title: 'leads',
          icon: PiUserFocus,
          moduleId: 'leads',
          requiredResources: ['moduleresources_leads'],
        },

        {
          title: 'crm',
          icon: FunnelPlotOutlined,
          moduleId: 'crm',
          requiredResources: ['moduleresources_crm'],
        },
      ],
    },
  ],

  FIREBASE_CONFIG: JSON.parse(import.meta.env.VITE_APPFUNNEL_FIREBASE_CONFIG),
  MOCKUP: {
    AUTH: {
      SIGNIN: {
        email: 'me@biruel.dev',
        password: 'idadm123',
      },
      SIGNUP: {
        countryCode: 'BR',
        name: 'Suporte Funnel',
        email: 'me@biruel.dev',
        emailCode: 1234,
        emailConfirmation: true,
        mobileCode: 1234,
        mobileConfirmation: true,
        password: 'idadm123',
      },
      RESET_PASSWORD: {
        email: 'me@biruel.dev',
        emailCode: 1234,
        password: 'idadm123',
      },
    },
  },
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default APP_FUNNEL;
